import { createModel } from '@rematch/core';
import { Card } from '@braze/web-sdk';
import type { RootModel } from './models';
import { Banner } from './ContentCards.type';

export type ContentCardState = {
  loading: boolean;
  contentCards: (Card)[];
};

export interface TimelineBannerParams {
  [key: string]: string;
  type: 'timelineBanner';
  eventName?: string;
  route?: string;
  routeParams?: string;
}

export interface TimelineBannerCard extends Banner<TimelineBannerParams> {
  image?: string;
}

const model = createModel<RootModel>()({
  name: 'contentCards',

  state: {
    loading: false,
    contentCards: null,
  } as ContentCardState,

  reducers: {
    onLoading: ((state: ContentCardState) => ({
      ...state,
      loading: true,
    })),

    onAllLoaded: ((state: ContentCardState) => ({
      ...state,
      loading: false,
    })),

    onCardsLoaded: ((
      state: ContentCardState,
      contentCards: Card[],
    ) => ({
      ...state,
      contentCards,
    })),
  },

  effects: dispatch => ({
    populateCards: (
      { cards }: { cards: TimelineBannerCard[] },
    ): void => {
      dispatch.contentCards.onLoading();
      dispatch.contentCards.onCardsLoaded(cards.filter(c => !c.dismissed));
      dispatch.contentCards.onAllLoaded();
    },
    removeCardById: (id: string, rootState): void => {
      dispatch.contentCards.onLoading();
      const newCards = rootState.contentCards.contentCards.filter(c => c.id !== id);
      dispatch.contentCards.onCardsLoaded(newCards);
      dispatch.contentCards.onAllLoaded();
    },
    removeCardsByType: (cardType: string, rootState): void => {
      dispatch.contentCards.onLoading();
      const newCards = rootState.contentCards.contentCards.filter(
        c => (c.extras?.type !== cardType),
      );
      dispatch.contentCards.onCardsLoaded(newCards);
      dispatch.contentCards.onAllLoaded();
    },
  }),

  selectors: (slice, createSelector, hasProps) => ({
    allCards() {
      return createSelector(
        slice((state: ContentCardState) => state?.contentCards),
        (cards: [Card]) => cards,
      );
    },
    latestBannerCardOfType: hasProps((
      _,
      { cardType }: { cardType: string },
    ) => createSelector(
      slice((state: ContentCardState) => state?.contentCards),
      (cards: [TimelineBannerCard]) => cards?.reduce((a, c) => (
        c.extras?.type === cardType && !(a?.updated > c.updated) ? c : a
      ), null),
    )),
  }),
});

export default model;
